import { createAppError } from '../../api/errors';
import {
  partnerCreditApprovalFixture,
  partnerCreditApprovalId,
  partnerCreditCertificationFixture,
  partnerCreditConfigurationFixture,
  partnerCreditPartnerId,
  partnerCreditVersionFixture,
} from './fixtures';
import type { PartnerCreditRepository } from './repository';
import type { PartnerCreditConfiguration, PartnerCreditVersion } from './types';

export interface MockPartnerCreditRepositoryOptions {
  readonly publishFailures?: number;
}

export class MockPartnerCreditRepository implements PartnerCreditRepository {
  readonly approvalId = partnerCreditApprovalId;
  readonly createVersionIdempotencyKeys: string[] = [];
  readonly certificationCalls: string[] = [];
  readonly statusCalls: Parameters<PartnerCreditRepository['changeStatus']>[1][] = [];
  healthCalls = 0;
  #publishFailures: number;
  #configuration: PartnerCreditConfiguration = partnerCreditConfigurationFixture;
  #versions: PartnerCreditVersion[] = [partnerCreditVersionFixture];

  constructor(options: MockPartnerCreditRepositoryOptions = {}) {
    this.#publishFailures = options.publishFailures ?? 0;
  }

  async get(partnerId: string, signal: AbortSignal): Promise<PartnerCreditConfiguration> {
    aborted(signal);
    if (partnerId === partnerCreditPartnerId) return clone(this.#configuration);
    return {
      id: null,
      partnerId,
      name: null,
      status: null,
      currentVersion: null,
      createdAt: null,
      createdBy: null,
      updatedAt: null,
      updatedBy: null,
    };
  }

  async listVersions(partnerId: string, signal: AbortSignal) {
    aborted(signal);
    return partnerId === partnerCreditPartnerId ? this.#versions.map(clone) : [];
  }

  async preview(_partnerId: string, input: Parameters<PartnerCreditRepository['preview']>[1]) {
    return {
      configuration: publicConfiguration(input),
      previewHash: 'P'.repeat(43),
      certificationRequired: true as const,
    };
  }

  async createVersion(
    _partnerId: string,
    _input: Parameters<PartnerCreditRepository['createVersion']>[1],
    idempotencyKey: string,
  ) {
    this.createVersionIdempotencyKeys.push(idempotencyKey);
    if (this.#publishFailures > 0) {
      this.#publishFailures -= 1;
      throw createAppError({
        code: 'PARTNER_CREDIT_VERSION_CONFLICT',
        fieldErrors: {},
        message: 'internal mock secret details',
        requestId: 'req-publish-conflict',
        retryAfterSeconds: null,
        status: 409,
      });
    }
    return { data: partnerCreditApprovalFixture, requestId: 'req-publish-created' };
  }

  async certify(_partnerId: string, input: Parameters<PartnerCreditRepository['certify']>[1]) {
    this.certificationCalls.push(input.approvalRequestId);
    return { data: partnerCreditCertificationFixture, requestId: 'req-certification' };
  }

  async changeStatus(
    _partnerId: string,
    input: Parameters<PartnerCreditRepository['changeStatus']>[1],
  ) {
    this.statusCalls.push(input);
    this.#configuration = { ...this.#configuration, status: input.status };
    return { data: clone(this.#configuration), requestId: 'req-status' };
  }
}

function publicConfiguration(input: Parameters<PartnerCreditRepository['preview']>[1]) {
  const { secret: _secret, reason: _reason, ...configuration } = input;
  void _secret;
  void _reason;
  return configuration;
}

function clone<Value>(value: Value): Value {
  return structuredClone(value);
}

function aborted(signal: AbortSignal) {
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
}
